import { useCallback, useMemo, useState } from 'react';

import { useRelatedWorks } from './useRelatedWorks';
import { User, Work } from './types';

type UseAuthorWorksInterface = {
  bookmarkAuthorWork: (work: Work) => void;
  followAuthorInAuthorWorks: (author: User) => void;
  setAuthorWorks: (
    author: string,
    works: Work[],
    count: number,
    url: string,
  ) => void;
  authorWorks: {
    author: string;
    count: number;
    url: string;
    works: Work[];
  } | null;
};

export const useAuthorWorks = (
  initialAuthorWorks: {
    count: number;
    url: string;
    works: Work[];
  } | null = null,
): UseAuthorWorksInterface => {
  const [author, setAuthor] = useState<string | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const [url, setUrl] = useState<string | null>(null);

  const { bookmarkWork, followWorkAuthor, relatedWorks, setRelatedWorks } =
    useRelatedWorks(initialAuthorWorks?.works || []);

  const setAuthorWorks = useCallback(
    (author: string, works: Work[], count: number, url: string) => {
      setAuthor(author);
      setRelatedWorks(works);
      setCount(count);
      setUrl(url);
    },
    [setAuthor, setCount, setUrl, setRelatedWorks],
  );

  const authorWorks = useMemo(() => {
    if (author == null || count == null || url == null) return null;
    return {
      author: author,
      count: count,
      url: url,
      works: relatedWorks,
    };
    // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, url, relatedWorks]);

  return {
    bookmarkAuthorWork: bookmarkWork,
    followAuthorInAuthorWorks: followWorkAuthor,
    setAuthorWorks: setAuthorWorks,
    authorWorks: authorWorks,
  };
};
